import { Dialog, InnerHTMLDiv } from '@myosh/odin-components';
import React from 'react';
import type { RiskMatrixData, RiskMatrixDataItem } from '../../../@types/risk-matrix-field';
import { cloneDeep } from 'lodash';
import { findRiskMatrixCaption } from './risk-rating-functions';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

const sanitizeHtml = (value?: string) => {
  return value ? DOMPurify.sanitize(value, { USE_PROFILES: { html: true } }) : '';
};

interface RiskRatingMatrixInfoDialogProps {
  isVisible: boolean;
  type: 'consequence' | 'likelihood' | undefined;
  data: Array<RiskMatrixData>;
  onClose: () => void;
}

function RiskRatingMatrixInfoDialog({ isVisible, type, data, onClose }: RiskRatingMatrixInfoDialogProps) {
  const { t } = useTranslation();

  const createDialogContentCard = (riskMatrixCellData: RiskMatrixDataItem, index: number) => {
    if (riskMatrixCellData) {
      const matrixCellCaption = findRiskMatrixCaption(riskMatrixCellData.caption);
      const matrixCellDescriptionCaption = findRiskMatrixCaption(riskMatrixCellData.descriptionCaption);

      return (
        <div className="mb-4 rounded-lg border border-gray-4 p-4 shadow-sm" key={index}>
          <p className="mb-2 font-bold">{matrixCellCaption}</p>
          <InnerHTMLDiv html={sanitizeHtml(matrixCellDescriptionCaption)} />
        </div>
      );
    }
  };

  const createDialogContent = () => {
    const dataCopy = cloneDeep(data);
    const headerCells = dataCopy[0].slice(1); // Exclude the first cell which is usually empty
    dataCopy.shift();
    const rowDescriptionCells = dataCopy.map((row) => row[0]);

    if (type === 'consequence') {
      return <>{headerCells.map(createDialogContentCard)}</>;
    }

    if (type === 'likelihood') {
      return <>{rowDescriptionCells.map(createDialogContentCard)}</>;
    }
  };

  return (
    <>
      {isVisible && (
        <Dialog header={t(type!)} containerClassName="max-w-[800px]" visible={isVisible} onClose={onClose}>
          {createDialogContent()}
        </Dialog>
      )}
    </>
  );
}

export default RiskRatingMatrixInfoDialog;
